'use client';

import { useState, useEffect } from 'react';
import { calculateSlidesPerView } from '@/app/_lib/breakpoint-utils';

/**
 * Hook to get current breakpoint-aware slides per view
 * Matches the responsive CSS classes: mobile=1, tablet(md)=2, desktop(lg)=slidesPerView
 * Returns 1 initially (most restrictive) to avoid hydration mismatch
 */
export function useResponsiveSlidesPerView(configuredSlidesPerView: number): number {
    // Start with 1 (mobile) to be safe during SSR/hydration
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(1);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const updateSlidesPerView = () => {
            setCurrentSlidesPerView(calculateSlidesPerView(window.innerWidth, configuredSlidesPerView));
        };

        const handleResize = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(updateSlidesPerView, 150);
        };

        // Set initial value immediately
        updateSlidesPerView();

        // Listen to resize with debouncing
        window.addEventListener('resize', handleResize);
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [configuredSlidesPerView]);

    return currentSlidesPerView;
}
